export const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const normalizeResult = (value) =>
  String(value || "")
    .trim()
    .toUpperCase();

export const getId = (competitor) => {
  const id = Number(competitor?.id);
  return Number.isFinite(id) ? id : null;
};

export const isRealCompetitor = (competitor) =>
  !!competitor && !competitor.pending && getId(competitor) !== null;

export const getFullName = (competitor) => {
  if (!competitor) return "";
  if (competitor.pending) return "En attente de combat";

  const nom = competitor.nom_competiteur || "";
  const prenom = competitor.prenom_competiteur || "";

  return `${nom} ${prenom}`.trim() || "Combattant";
};

export const getClubName = (competitor) => {
  if (!competitor || competitor.pending) return "";
  return competitor.club_competiteur || competitor.club || "";
};

export const findCombat = (combats = [], id1, id2) => {
  const a = Number(id1);
  const b = Number(id2);

  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

  return (
    combats.find((combat) => {
      const c1 = Number(combat.competiteur_id1);
      const c2 = Number(combat.competiteur_id2);

      return (c1 === a && c2 === b) || (c1 === b && c2 === a);
    }) || null
  );
};

export const getWinnerIdFromCombat = (combat) => {
  if (!combat) return null;

  const id1 = Number(combat.competiteur_id1);
  const id2 = Number(combat.competiteur_id2);

  if (!Number.isFinite(id1) || !Number.isFinite(id2)) return null;

  const r1 = normalizeResult(combat.resultat_competiteur1);
  const r2 = normalizeResult(combat.resultat_competiteur2);

  if (r1 === "V") return id1;
  if (r2 === "V") return id2;

  if (["D", "F", "H"].includes(r1) && !["D", "F", "H"].includes(r2)) {
    return id2;
  }

  if (["D", "F", "H"].includes(r2) && !["D", "F", "H"].includes(r1)) {
    return id1;
  }

  const p1 =
    combat.points_competiteur1 !== null &&
    combat.points_competiteur1 !== undefined
      ? Number(combat.points_competiteur1)
      : null;

  const p2 =
    combat.points_competiteur2 !== null &&
    combat.points_competiteur2 !== undefined
      ? Number(combat.points_competiteur2)
      : null;

  if (Number.isFinite(p1) && Number.isFinite(p2)) {
    if (p1 > p2) return id1;
    if (p2 > p1) return id2;
  }

  return null;
};

export const getResultForCompetitor = (combat, competitor) => {
  if (!combat || !competitor) {
    return { result: "", points: null };
  }

  const id = getId(competitor);
  if (!id) {
    return { result: "", points: null };
  }

  const id1 = Number(combat.competiteur_id1);
  const id2 = Number(combat.competiteur_id2);

  if (id === id1) {
    return {
      result: normalizeResult(combat.resultat_competiteur1),
      points: combat.points_competiteur1,
    };
  }

  if (id === id2) {
    return {
      result: normalizeResult(combat.resultat_competiteur2),
      points: combat.points_competiteur2,
    };
  }

  return { result: "", points: null };
};

const makePairs = (list = [], roundIndex = 0) => {
  const pairs = [];

  for (let i = 0; i < list.length; i += 2) {
    pairs.push({
      roundIndex,
      matchIndex: i / 2,
      competiteur1: list[i] || null,
      competiteur2: list[i + 1] || null,
      position1: i + 1,
      position2: i + 2,
    });
  }

  return pairs;
};

const getGeneratedRoundTitle = (baseSize, roundIndex) => {
  const matchCount = Math.max(1, baseSize / Math.pow(2, roundIndex + 1));

  if (matchCount === 1) return "Finale";
  if (matchCount === 2) return "1/2 finale";
  if (matchCount === 4) return "1/4 finale";
  if (matchCount === 8) return "1/8 finale";
  if (matchCount === 16) return "1/16 finale";
  if (matchCount === 32) return "1/32 finale";

  return `1/${matchCount} finale`;
};

const getRoundSubtitle = (matchCount) => {
  if (matchCount === 1) return "Vainqueur";
  return `${matchCount * 2} combattants`;
};

export const buildBracketRounds = ({
  seed = [],
  competiteurs = [],
  combats = [],
  roundNames = [],
}) => {
  const startingSeed = Array.isArray(seed) && seed.length ? seed : competiteurs;
  const baseSize = Math.max(2, startingSeed.length);

  const byId = new Map(
    (competiteurs || [])
      .filter((competitor) => getId(competitor) !== null)
      .map((competitor) => [getId(competitor), competitor]),
  );

  const rounds = [];
  let current = startingSeed;
  let roundIndex = 0;

  while (current.length > 1 && roundIndex < 12) {
    const pairs = makePairs(current, roundIndex);

    const enrichedPairs = pairs.map((pair) => {
      const c1 = pair.competiteur1;
      const c2 = pair.competiteur2;

      const combat =
        isRealCompetitor(c1) && isRealCompetitor(c2)
          ? findCombat(combats, getId(c1), getId(c2))
          : null;

      const winnerId = getWinnerIdFromCombat(combat);
      const winner = winnerId ? byId.get(winnerId) || null : null;

      const autoWinner =
        isRealCompetitor(c1) && !c2
          ? c1
          : !c1 && isRealCompetitor(c2)
          ? c2
          : null;

      return {
        ...pair,
        combat,
        winnerId,
        winner,
        autoWinner,
      };
    });

    rounds.push({
      roundIndex,
      title:
        roundNames?.[roundIndex] ||
        getGeneratedRoundTitle(baseSize, roundIndex),
      subtitle: getRoundSubtitle(pairs.length),
      matchCount: pairs.length,
      pairs: enrichedPairs,
    });

    const nextRound = pairs.map((pair) => {
      const c1 = pair.competiteur1;
      const c2 = pair.competiteur2;

      if (isRealCompetitor(c1) && !c2) return c1;
      if (!c1 && isRealCompetitor(c2)) return c2;

      if (isRealCompetitor(c1) && isRealCompetitor(c2)) {
        const combat = findCombat(combats, getId(c1), getId(c2));
        const winnerId = getWinnerIdFromCombat(combat);

        if (winnerId && byId.has(winnerId)) {
          return byId.get(winnerId);
        }

        return {
          pending: true,
          from: `${roundIndex}-${pair.matchIndex}`,
        };
      }

      return {
        pending: true,
        from: `${roundIndex}-${pair.matchIndex}`,
      };
    });

    current = nextRound;
    roundIndex += 1;
  }

  return rounds;
};

export const getBracketStats = (seed = [], rounds = []) => {
  const realCompetitorCount = seed.filter(isRealCompetitor).length;

  const allPairs = rounds.flatMap((round) => round.pairs);

  const playablePairs = allPairs.filter(
    (pair) =>
      isRealCompetitor(pair.competiteur1) &&
      isRealCompetitor(pair.competiteur2),
  );

  const completedPairs = playablePairs.filter((pair) => !!pair.combat);

  const currentPlayablePairs = playablePairs.filter((pair) => {
    if (pair.combat) return false;
    if (pair.competiteur1?.pending || pair.competiteur2?.pending) return false;
    return true;
  });

  return {
    baseSize: Math.max(2, seed.length),
    realCompetitorCount,
    completedCount: completedPairs.length,
    toPlayCount: currentPlayablePairs.length,
  };
};